'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

import { useInView } from '@/hooks/use-in-view';

type Props = {
  children: ReactNode;
  /** The sticky frame (heading + steps). */
  frameClassName?: string;
};

const HEADER = 80;
const PHONES = '(max-width: 767px)';

/**
 * Phones (UX Design prototype): the process section pins under the header and scrolling down
 * moves the steps sideways. The frame is sticky; a spacer after it adds exactly the distance the
 * steps travel; the steps (`[data-proc-steps]`, server-rendered) get a transform per rAF. Without
 * JS, from sm and under reduced motion nothing is pinned and the steps simply scroll sideways
 * (`[data-proc-viewport]` is `overflow-x: auto` until `data-pinned` is set). Layout is read only
 * once the section nears the viewport, on resize and when fonts load.
 */
export const ProcessPin = ({ children, frameClassName }: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(rootRef);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    const frame = frameRef.current;
    const spacer = spacerRef.current;
    const viewport = frame?.querySelector<HTMLElement>('[data-proc-viewport]');
    const steps = frame?.querySelector<HTMLElement>('[data-proc-steps]');
    if (!isInView || !root || !frame || !spacer || !viewport || !steps) return undefined;

    const phones = window.matchMedia(PHONES);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let distance = 0;
    let top = HEADER;
    let raf = 0;

    const update = () => {
      raf = 0;
      if (!distance) return;
      const progress = Math.max(0, Math.min(1, (top - root.getBoundingClientRect().top) / distance));
      steps.style.transform = `translate3d(${(-progress * distance).toFixed(1)}px, 0, 0)`;
    };
    const request = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    const layout = () => {
      if (!phones.matches || reduce.matches) {
        distance = 0;
        spacer.style.height = '0px';
        steps.style.transform = '';
        frame.style.top = '';
        setIsPinned(false);
        return;
      }
      steps.style.transform = 'none';
      // Pinned frame centred in the visible part of the screen, under the header.
      top = Math.max(HEADER, Math.round(HEADER + (window.innerHeight - HEADER - frame.offsetHeight) / 2));
      frame.style.top = `${top}px`;
      distance = Math.max(0, Math.ceil(steps.scrollWidth - viewport.clientWidth));
      spacer.style.height = `${distance}px`;
      setIsPinned(distance > 0);
      update();
    };

    layout();
    document.fonts?.ready.then(layout);
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', layout);
    phones.addEventListener('change', layout);
    reduce.addEventListener('change', layout);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', layout);
      phones.removeEventListener('change', layout);
      reduce.removeEventListener('change', layout);
    };
  }, [isInView]);

  return (
    <div ref={rootRef} data-pinned={isPinned ? '' : undefined}>
      <div ref={frameRef} className={frameClassName}>
        {children}
      </div>
      <div ref={spacerRef} aria-hidden="true" />
    </div>
  );
};
